import { Component, OnInit, Input, ViewChild,ChangeDetectorRef  } from '@angular/core';
import { Route, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { AlertService } from 'src/app/services/alerts.service';
import { ConversationService } from 'src/app/services/conversation.service';
import { UserService } from 'src/app/services/user.service';
import { finalize } from 'rxjs/operators';
import { NativeNotificationService } from 'src/app/services/native-notification.service';
import { applyAlertFilters } from 'src/app/utils/alert-filtering';

@Component({
    selector: 'comp-alerts',
    templateUrl: './alerts.component.html',
    styleUrls: ['./alerts.component.scss'],
})
export class AlertComponent implements OnInit {
    // Alerts
    @Input() alerts: any = [];
    filteredAlerts: any = [];
    user: any;
    showOpportunities: boolean = true;
    showLeases: boolean = true;
    showMissedCalls: boolean = true;
    showUnassignedOpps: boolean = true;
    opportunityCount: number = 0;
    leaseCount: number = 0;
    missedCallCount: number = 0;
    unassignedOppCount: number = 0;
    isLoading: boolean = false;
    searchTerm: string = '';
    locations: any[] = [];
    selectedLocationId: string = 'all';
    assignees: any[] = [];
    selectedAssigneeId: string = 'all';
    selectedUnreadType: string = 'all';
    isManager: boolean = false;
    isMarkingRead: boolean = false;
    emailCount: number = 0;
    smsCount: number = 0;
    assigneeCounts: Record<string, number> = {};
    allAssigneeCount: number = 0;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private alertService: AlertService,
        private userService: UserService,
        private router: Router,
        private modalCtrl: ModalController,
        private convoService: ConversationService,
        private nativeNotifications: NativeNotificationService
    ) {
        this.onAlertClick = this.onAlertClick.bind(this);
        this.onViewAllClick = this.onViewAllClick.bind(this);
    }

    ngOnInit(): void {
        this.user = JSON.parse(localStorage.getItem('user')) || {};
        this.isManager = this.userService.admin === 1;
        if (this.isManager) {
            const currentUserId = this.userService.id || this.user?.id;
            this.selectedAssigneeId = currentUserId ? String(currentUserId) : 'all';
        }
        if (!this.isManager) {
            this.nativeNotifications.clearAlertNotifications();
        }
        this.getDealerLocations();
        this.getAlertAssignees();
        this.getAlerts();
    }

    getDealerLocations(): void {
        if (!this.isManager) {
            return;
        }

        this.alertService.getDealerLocations().subscribe(
            (resp: any) => {
                this.locations = resp?.locations || [];
            },
            () => {
                this.locations = [];
            }
        );
    }

    getAlertAssignees(): void {
        if (!this.isManager) {
            return;
        }

        this.alertService.getAlertAssignees().subscribe(
            (resp: any) => {
                this.assignees = resp?.users || [];
            },
            () => {
                this.assignees = [];
            }
        );
    }

    // Alerts
    getAlerts(): void {
        this.isLoading = true;
        this.alertService.getAlerts({
            read: 0
        })
            .pipe(finalize(() => {
                this.isLoading = false;
                this.changeDetector.detectChanges();
            }))
            .subscribe(
                (resp) => {
                    this.alerts = resp['alerts'];
                    this.filterAlerts();
                    this.changeDetector.detectChanges();
                    //console.log(this.alerts);
                },
                (error) => {
                    console.error('Error loading alerts:', error);
                }
            );
        
    }

    onLocationChange(): void {
        this.filterAlerts();
    }

    onAssigneeChange(): void {
        this.filterAlerts();
    }

    onUnreadTypeChange(): void {
        this.filterAlerts();
    }

    get allAlertTypesSelected(): boolean {
        return this.showOpportunities && this.showLeases && this.showUnassignedOpps;
    }

    toggleAllAlertTypes(checked: boolean): void {
        this.showOpportunities = checked;
        this.showLeases = checked;
        this.showUnassignedOpps = checked;
        this.filterAlerts();
    }

    markAllRead(): void {
        if (this.isMarkingRead || this.alerts.length === 0) {
            return;
        }

        this.isMarkingRead = true;
        this.alertService.markAlertsRead({
            read: 0,
            location_id: this.selectedLocationId,
            assignee_id: this.selectedAssigneeId,
            alert_type: this.selectedUnreadType
        })
            .pipe(finalize(() => {
                this.isMarkingRead = false;
                this.changeDetector.detectChanges();
            }))
            .subscribe(
                (response: any) => {
                    this.deductCurrentUserAlertCount(response?.count);
                    this.getAlerts();
                },
                (error) => console.error('Error marking alerts as read:', error)
            );
    }

    filterAlerts(): void {
        const result = applyAlertFilters(this.alerts, {
            searchTerm: this.searchTerm,
            selectedLocationId: this.selectedLocationId,
            selectedAssigneeId: this.selectedAssigneeId,
            selectedUnreadType: this.selectedUnreadType,
            showOpportunities: this.showOpportunities,
            showLeases: this.showLeases,
            showUnassignedOpps: this.showUnassignedOpps,
        });

        this.filteredAlerts = result.filteredAlerts;
        this.opportunityCount = result.opportunityCount;
        this.leaseCount = result.leaseCount;
        this.unassignedOppCount = result.unassignedOppCount;
        this.emailCount = result.emailCount;
        this.smsCount = result.smsCount;
        this.missedCallCount = result.missedCallCount;
        this.assigneeCounts = result.assigneeCounts;
        this.allAssigneeCount = result.allAssigneeCount;
    }

    getAssigneeCount(assigneeId: number): number {
        return this.assigneeCounts[String(assigneeId)] || 0;
    }

    // Alert Click
    async onAlertClick(alert): Promise<void> {
        const id = this.getAlertConversationId(alert);

        if (!id) {
            console.error('Unable to open alert without conversation id', alert);
            return;
        }
    
        try {
            // Wait for the timestamp update to finish
            await this.updateConversationTimestamp(id);
    
            // Wait for the alert status update to finish
            const readResult = await this.setAlertStatus(alert.id);

            if (readResult?.marked_read) {
                this.removeAlertFromList(alert.id);
                this.deductCurrentUserAlertCount(readResult?.marked_count);
            }
    
            // Dismiss the modal after all updates are completed
            await this.modalCtrl.dismiss();

            await this.router.navigate(['conversations'], {
                queryParams: { id },
            });
    
            // Emit the selected conversation ID
            this.convoService.emitActiveConversation(id);
        } catch (error) {
            console.error('Error handling alert click:', error);
        }
    }

    private getAlertConversationId(alert: any): number | null {
        const conversationId = Number(alert?.conversation?.id);

        if (conversationId) {
            return conversationId;
        }

        const pathConversationId = Number(String(alert?.path || '').split('?id=')[1]);

        return pathConversationId || null;
    }

    private removeAlertFromList(alertId: number): void {
        this.alerts = this.alerts.filter((item) => Number(item.id) !== Number(alertId));
        this.filterAlerts();
    }

    private deductCurrentUserAlertCount(markedCount: number): void {
        const count = Math.max(0, Number(markedCount || 0));
        this.userService.alertCount = Math.max(
            0,
            Number(this.userService.alertCount || 0) - count
        );
    }

    updateConversationTimestamp(id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.alertService.updateConversationTimestamp(id).subscribe(
                () => resolve(),
                (error) => reject(error)
            );
        });
    }
    
    setAlertStatus(alertId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.alertService.setAlertStatus(alertId).subscribe(
                (response) => resolve(response),
                (error) => reject(error)
            );
        });
    }
    // onAlertClick(alert): void {
    //     console.log('Alert clicked:', alert);

    //     let id = alert.path.split('?id=')

    //     console.log('Extracted ID:', id[1]);

    //     this.alertService.setAlertStatus(alert.id).subscribe((resp) => {
    //         // Dismiss Modal
    //         this.modalCtrl.dismiss();

    //         // Load Alert
    //         this.router.navigate(['redirect'], { 
    //             queryParams : { id: id[1], storeChange: alert.dealer_id}
    //         });

    //     });
    // }

    onViewAllClick(): void {
        this.modalCtrl.dismiss();

        this.router.navigate(['alerts']);
    }

    onCloseClick(): void {
        this.modalCtrl.dismiss();
    }
}
